import React, { useEffect, useRef, useState } from "react";
import Header from "../layouts/Header";
import Footer from "../layouts/Footer";
import { Card, Col, Row, Form, Button, InputGroup, Table, Badge, Spinner, Alert } from "react-bootstrap";
import { Link } from "react-router-dom";
import axiosInstance from "../services/axiosInterceptor";
import StudentInfoForm from "../students/StudentInfoForm";
import {
  resolveUploadUrl,
  genderLabel,
  statusLabel,
  generalClassLabel,
  islamicClassLabel,
  avatarInitial,
  dormLabel,
} from "../students/studentLabels";

export default function EditStudentInfo() {
  ///// Skin Switch /////
  const currentSkin = (localStorage.getItem('skin-mode')) ? 'dark' : '';
  const [skin, setSkin] = useState(currentSkin);

  const switchSkin = (skin) => {
    if (skin === 'dark') {
      const btnWhite = document.getElementsByClassName('btn-white');
      for (const btn of btnWhite) {
        btn.classList.add('btn-outline-primary');
        btn.classList.remove('btn-white');
      }
    } else {
      const btnOutlinePrimary = document.getElementsByClassName('btn-outline-primary');
      for (const btn of btnOutlinePrimary) {
        btn.classList.remove('btn-outline-primary');
        btn.classList.add('btn-white');
      }
    }

    const tables = document.getElementsByTagName('table');
    for (const table of tables) {
      const theads = table.querySelectorAll('thead');
      if (skin === 'dark') {
        for (const thead of theads) {
          thead.classList.remove('table-secondary');
          thead.classList.add('table-primary');
        }
        table.classList.add('table-dark');
      } else {
        table.classList.remove('table-dark');
        for (const thead of theads) {
          thead.classList.remove('table-primary');
          thead.classList.add('table-secondary');
        }
      }
    }
  };

  switchSkin(skin);

  useEffect(() => {
    switchSkin(skin);
  }, [skin]);

  const [searchKey, setSearchKey] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [searched, setSearched] = useState(false);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState(null);
  const inputRef = useRef(null);

  const handleSearch = async (e) => {
    e.preventDefault();
    const key = searchKey.trim();
    if (key.length < 2) {
      setError('กรุณากรอกคำค้นหาอย่างน้อย 2 ตัวอักษร');
      return;
    }
    setError('');
    setSearching(true);
    setSearched(false);
    setResults([]);
    setSelected(null);
    try {
      const res = await axiosInstance.get(`/api/students/search?q=${encodeURIComponent(key)}`);
      if (res.data.success) {
        setResults(res.data.data);
        setSearched(true);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'เกิดข้อผิดพลาดในการค้นหา');
    } finally {
      setSearching(false);
    }
  };

  const handleClear = () => {
    setSearchKey('');
    setResults([]);
    setSearched(false);
    setError('');
    setSelected(null);
    inputRef.current?.focus();
  };

  // ชื่อ/เลขประจำตัวในผลค้นหาต้องตรงกับที่เพิ่งบันทึก ไม่ใช่ค่าตอนที่ค้นหา
  // (แก้ชื่อแล้วกดกลับไปผลค้นหาแล้วเห็นชื่อเดิม จะไม่แน่ใจว่าบันทึกไปแล้วหรือยัง)
  const handleSaved = (saved) => {
    setResults(prev => prev.map(st => (
      String(st.st_id) === String(selected?.st_id)
        ? { ...st, st_name: saved.st_name, st_idnumber: saved.st_idnumber, st_gen: saved.st_gen, st_photo: saved.st_photo }
        : st
    )));
    setSelected(prev => (prev ? { ...prev, st_name: saved.st_name, st_idnumber: saved.st_idnumber } : prev));
  };

  return (
    <React.Fragment>
      <Header onSkin={setSkin} />
      <div className="main main-app p-3 p-lg-4">
        <div className="d-sm-flex align-items-center justify-content-between mb-4">
          <div>
            <ol className="breadcrumb fs-sm mb-1">
              <li className="breadcrumb-item"><Link to="#">ทะเบียนและหลักสูตร</Link></li>
              <li className="breadcrumb-item active" aria-current="page">แก้ไขข้อมูลนักเรียน</li>
            </ol>
            <h4 className="main-title mb-0">แก้ไขข้อมูลนักเรียน</h4>
          </div>
        </div>

        {error && (
          <Alert variant="danger" onClose={() => setError('')} dismissible>
            {error}
          </Alert>
        )}

        <Card className="card-one mb-4">
          <Card.Body>
            <Form onSubmit={handleSearch}>
              <Form.Label><strong>ค้นหานักเรียนที่ต้องการแก้ไข</strong></Form.Label>
              <InputGroup>
                <Form.Control
                  ref={inputRef}
                  type="text"
                  value={searchKey}
                  onChange={e => setSearchKey(e.target.value)}
                  placeholder="เลขประจำตัวนักเรียน หรือชื่อ-สกุล..."
                  autoFocus
                />
                {searchKey && (
                  <Button variant="outline-secondary" onClick={handleClear} type="button">
                    ล้าง
                  </Button>
                )}
                <Button variant="primary" type="submit" disabled={searching}>
                  {searching ? <Spinner animation="border" size="sm" /> : <><i className="ri-search-2-line me-1"></i>ค้นหา</>}
                </Button>
              </InputGroup>
              <Form.Text className="text-muted">
                ค้นหาจากเลขประจำตัวนักเรียน หรือชื่อ-สกุล (แสดงผลสูงสุด 30 รายการ)
              </Form.Text>
            </Form>
          </Card.Body>
        </Card>

        {searched && results.length === 0 && (
          <Alert variant="warning">ไม่พบนักเรียนที่ค้นหา</Alert>
        )}

        {results.length > 0 && !selected && (
          <Card className="card-one mb-4">
            <Card.Header>
              <Card.Title as="h6">ผลการค้นหา {results.length} รายการ</Card.Title>
            </Card.Header>
            <Card.Body className="p-0">
              <div className="table-responsive">
                <Table className="mb-0 align-middle" hover>
                  <thead className="table-secondary">
                    <tr>
                      <th>นักเรียน</th>
                      <th>เลขประจำตัว</th>
                      <th>ชั้นสามัญ</th>
                      <th>ชั้นศาสนา</th>
                      <th>หอพัก</th>
                      <th>สถานะ</th>
                      <th className="text-end">แก้ไข</th>
                    </tr>
                  </thead>
                  <tbody>
                    {results.map(st => {
                      const photoUrl = resolveUploadUrl(st.st_photo);
                      const dorm = dormLabel(st);
                      return (
                        <tr key={st.st_id}>
                          <td>
                            <div className="d-flex align-items-center gap-2">
                              {photoUrl ? (
                                <img
                                  src={photoUrl}
                                  alt={st.st_name}
                                  className="rounded-circle flex-shrink-0"
                                  style={{ width: 36, height: 36, objectFit: 'cover' }}
                                />
                              ) : (
                                <div
                                  className="bg-primary text-white rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
                                  style={{ width: 36, height: 36, fontSize: 13, fontWeight: 600 }}
                                >
                                  {avatarInitial(st.st_name)}
                                </div>
                              )}
                              <div>
                                <div className="fw-medium">{st.st_name}</div>
                                <div>{genderLabel(st.st_gen)}</div>
                              </div>
                            </div>
                          </td>
                          <td>{st.st_idnumber}</td>
                          <td>{generalClassLabel(st)}</td>
                          <td>{islamicClassLabel(st)}</td>
                          <td className={dorm ? '' : 'text-muted'}>{dorm || 'ยังไม่ได้ระบุ'}</td>
                          <td>{statusLabel(st.st_status)}</td>
                          <td className="text-end">
                            <Button variant="outline-primary" size="sm" onClick={() => setSelected(st)}>
                              <i className="ri-edit-line me-1"></i>แก้ไข
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
          </Card>
        )}

        {selected && (
          <Row className="g-3 mb-4">
            <Col xl="12">
              <Card className="card-one">
                <Card.Header className="flex-wrap gap-2">
                  <div>
                    <Card.Title as="h6" className="mb-1">แก้ไขข้อมูล: {selected.st_name}</Card.Title>
                    <div className="fs-sm text-muted">
                      {selected.st_idnumber}
                      {generalClassLabel(selected) !== '-' && <> · {generalClassLabel(selected)}</>}
                    </div>
                  </div>
                  <div className="ms-auto d-flex align-items-center gap-2">
                    {selected.st_status === '3' && (
                      <Badge bg="warning">นักเรียนใหม่ — แก้ที่เมนูรับสมัครนักเรียนได้ด้วย</Badge>
                    )}
                    <Button variant="outline-secondary" size="sm" onClick={() => setSelected(null)}>
                      <i className="ri-arrow-left-line me-1"></i>กลับไปผลการค้นหา
                    </Button>
                  </div>
                </Card.Header>
                <Card.Body className="p-3">
                  <StudentInfoForm
                    key={selected.st_id}
                    studentID={selected.st_id}
                    onSaved={handleSaved}
                  />
                </Card.Body>
              </Card>
            </Col>
          </Row>
        )}

        <Footer />
      </div>
    </React.Fragment>
  );
}
